import { useParams, Link } from 'react-router';
import { useState, useEffect } from 'react';
import { Calendar, ArrowLeft } from 'lucide-react';
import { getNewsBySlug } from '@/services/api';
import type { NewsItem } from '@/data/news';
import { useLanguage } from '@/contexts/LanguageContext';
import 'react-quill/dist/quill.snow.css';

export function NewsDetailPage() {
  const { id: slug } = useParams<{ id: string }>();
  const [newsItem, setNewsItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const loadNewsItem = async () => {
      if (!slug) return;
      
      setLoading(true);
      const item = await getNewsBySlug(slug);
      setNewsItem(item);
      setLoading(false);
    };

    loadNewsItem();
  }, [slug]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'hu' ? 'hu-HU' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">
          {language === 'hu' ? 'Betöltés...' : 'Loading...'}
        </div>
      </div>
    );
  }

  if (!newsItem) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">
            {language === 'hu' ? 'Hír nem található' : 'News not found'}
          </h1>
          <Link to="/news" className="text-blue-600 hover:underline">
            {language === 'hu' ? 'Vissza a hírekhez' : 'Back to News'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-6">

        <article>
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl mb-6">
              {newsItem.title[language]}
            </h1>
            
            <div className="flex items-center gap-6 text-gray-600">
              <span className="flex items-center gap-2">
                <Calendar size={18} />
                {formatDate(newsItem.date)}
              </span>
            </div>
          </div>

          <div className="relative aspect-[16/9] mb-12 overflow-hidden">
            <img
              src={newsItem.image}
              alt={newsItem.title[language]}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="text-xl text-gray-700 leading-relaxed mb-8">
              {newsItem.excerpt[language]}
            </div>
            
            <div 
              className="ql-editor text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: newsItem.content[language] }}
            />
          </div>
        </article>

        <div className="mt-16 pt-8 border-t border-gray-200">
          <Link 
            to="/news" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors"
          >
            <ArrowLeft size={20} />
            {language === 'hu' ? 'Vissza a hírekhez' : 'Back to News'}
          </Link>
        </div>
      </div>
    </div>
  );
}